import React from "react";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";
import { iconFor } from "@/lib/icons";
import { resolveCtaHref, isExternalCta } from "@/lib/cta";
import { resolveUploadUrl } from "@/lib/api";
import { trackCta } from "@/lib/analytics";

export const BooksResources = () => {
    const { content } = useContent();
    const b = content.books;
    const items = (b.items || []).filter((it) => it.visible !== false);

    return (
        <section id="books-resources" data-testid="books-resources-section" className="bg-white py-24 lg:py-28">
            <div className="max-w-7xl mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                    <div className="lg:col-span-8">
                        <p className="label-eyebrow">{b.eyebrow}</p>
                        <h2 data-testid="books-resources-title" className="mt-4 font-display font-semibold text-slate-900 text-3xl sm:text-4xl lg:text-[42px] leading-[1.1] tracking-tight">{b.title}</h2>
                        <p className="mt-5 text-base lg:text-lg text-slate-600 font-body leading-relaxed">{b.subtitle}</p>
                    </div>
                    <div className="lg:col-span-4">
                        <p className="text-[13px] text-slate-500 border-l-2 border-teal-600 pl-4">{b.intro}</p>
                    </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {items.map((item) => {
                        const Icon = iconFor(item.icon);
                        const testId = `resource-card-${item.key}`;
                        // Books use the resource-specific WhatsApp template
                        const ctaEntry = { ...item, cta_kind: item.cta_kind === "whatsapp" ? "resource_whatsapp" : item.cta_kind };
                        const href = resolveCtaHref(ctaEntry, content.contact);
                        const external = isExternalCta(ctaEntry);
                        const cover = resolveUploadUrl(item.cover_url);
                        return (
                            <article key={item.key} data-testid={testId} className="card-lift bg-white border border-slate-200 flex flex-col">
                                <div className="relative h-40 bg-gradient-to-br from-slate-900 to-[#0F2540] border-b border-slate-200 flex items-center justify-center overflow-hidden">
                                    {cover ? (
                                        <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-90" onError={(e) => { e.target.style.display = "none"; }} />
                                    ) : (
                                        <>
                                            <div className="absolute inset-0 opacity-[0.08]">
                                                <div className="w-full h-full bg-[radial-gradient(circle_at_30%_30%,rgba(20,184,166,0.7),transparent_60%)]" />
                                            </div>
                                            <Icon strokeWidth={1.25} className="w-14 h-14 text-teal-300 relative z-10" />
                                        </>
                                    )}
                                    <span className="absolute top-3 left-3 text-[10.5px] uppercase tracking-[0.2em] text-teal-200/80 font-medium z-10 bg-slate-900/40 px-2 py-1">{item.tag}</span>
                                </div>
                                <div className="p-7 flex flex-col flex-1">
                                    <h3 className="font-display text-[17px] font-semibold text-slate-900 leading-snug">{item.title}</h3>
                                    <p className="mt-3 text-[14px] text-slate-600 font-body leading-relaxed flex-1">{item.desc}</p>
                                    {item.suitable && (
                                        <p className="mt-4 text-[12.5px] text-slate-500 leading-relaxed">
                                            <span className="font-semibold text-slate-700">Suitable for: </span>{item.suitable}
                                        </p>
                                    )}
                                    <div className="mt-6 pt-5 border-t border-slate-100">
                                        <a
                                            href={href}
                                            target={external ? "_blank" : undefined}
                                            rel={external ? "noopener noreferrer" : undefined}
                                            data-testid={`${testId}-cta`}
                                            onClick={() => trackCta("books_enquiry_click", { source: "books_resources_section", label: testId })}
                                            className="btn-link-teal"
                                        >
                                            {item.cta_label}
                                            <ArrowRight className="w-4 h-4" />
                                        </a>
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default BooksResources;
